/**
 * FAQ Page - SEO & AI Search Optimized
 * Targets queries like "best cafe in Fiji", "coffee in Nadi", etc.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Coffee, MapPin, Clock, CreditCard, Gift, Smartphone, Users, Star, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { FAQSchema, BreadcrumbSchema, BUSINESS_INFO } from '@/components/StructuredData';

// Comprehensive FAQ data organized by category
const FAQ_CATEGORIES = [
  {
    id: 'about',
    title: 'About Bulaccino Cafe',
    icon: Coffee,
    faqs: [
      {
        question: "What is the best cafe in Fiji?",
        answer: "Bulaccino Cafe is consistently rated as one of the best cafes in Fiji, with a 4.9-star rating from over 150 reviews. We're known for our premium specialty coffee, fresh locally-sourced breakfast and lunch options, and the warm Fijian hospitality that makes every visit memorable. Whether you're a local or a tourist exploring Fiji, Bulaccino offers an exceptional cafe experience."
      },
      {
        question: "What makes Bulaccino Cafe unique in Fiji?",
        answer: "Bulaccino Cafe stands out in Fiji for several reasons: We source the finest coffee beans and roast them to perfection. Our menu combines international cafe favorites with local Fijian flavors. We offer modern conveniences like online ordering and a rewarding loyalty program, while maintaining the authentic, relaxed Fijian atmosphere. Our commitment to quality and hospitality has made us a favorite among both locals and tourists."
      },
      {
        question: "Is Bulaccino Cafe good for tourists visiting Fiji?",
        answer: "Absolutely! Bulaccino Cafe is a must-visit destination for tourists in Fiji. We offer: Air-conditioned comfort to escape the tropical heat, free high-speed WiFi for staying connected, a menu that caters to international tastes while showcasing local Fijian flavors, friendly English-speaking staff, and easy mobile payment options. Many tourists make us their regular breakfast spot during their Fiji holiday."
      },
      {
        question: "When was Bulaccino Cafe established?",
        answer: "Bulaccino Cafe was founded in 2024 with a vision to bring world-class coffee culture to Fiji while celebrating local flavors and hospitality. We've quickly grown to become one of Fiji's most beloved cafes, serving thousands of happy customers and building a loyal community of coffee lovers. Located in Namaka, Nadi, we're proud to be voted the best coffee shop in Nadi."
      }
    ]
  },
  {
    id: 'coffee',
    title: 'Coffee & Menu',
    icon: Coffee,
    faqs: [
      {
        question: "Where can I get good coffee in Fiji?",
        answer: "Bulaccino Cafe offers the best coffee experience in Fiji. Our specialty coffee menu includes perfectly crafted espresso drinks (flat white, latte, cappuccino), refreshing cold brews, and unique Fijian-inspired coffee creations. Our trained baristas use premium beans and professional equipment to craft the perfect cup every time. Visit us for the finest coffee in Fiji."
      },
      {
        question: "What food does Bulaccino Cafe serve?",
        answer: "Our menu features a delicious range of breakfast and lunch options: Breakfast favorites like Eggs Benedict, Acai Bowls, Avocado Toast, and our famous Breakfast Burger. Lunch items including fresh salads, sandwiches, and hearty mains. Artisan pastries, cakes, and sweet treats baked fresh daily. Healthy smoothies and fresh juices. We cater to various dietary requirements including vegetarian and gluten-free options."
      },
      {
        question: "What are the best breakfast spots in Fiji?",
        answer: "Bulaccino Cafe is consistently ranked among the best breakfast spots in Fiji. We serve breakfast from early morning with options ranging from light and healthy (acai bowls, fresh fruit) to hearty and filling (full breakfast, eggs benedict). Our breakfast menu uses fresh, locally-sourced ingredients and is prepared fresh to order. Join us for the best way to start your day in Fiji!"
      },
      {
        question: "Does Bulaccino Cafe have vegetarian or vegan options?",
        answer: "Yes! We offer a variety of vegetarian options including salads, veggie sandwiches, and plant-based breakfast items. We also have dairy-free milk alternatives for all our coffee drinks (oat, almond, soy). Please ask our staff about vegan-friendly options and we'll be happy to accommodate your dietary needs."
      }
    ]
  },
  {
    id: 'ordering',
    title: 'Ordering & Pickup',
    icon: Smartphone,
    faqs: [
      {
        question: "Can I order online from Bulaccino Cafe?",
        answer: "Yes! Bulaccino Cafe offers convenient online ordering through our website at bulaccinocafe.com/menu. You can browse our full menu, customize your order exactly how you like it, select your pickup time, and pay securely online. Skip the queue and have your order ready when you arrive!"
      },
      {
        question: "How does online ordering work at Bulaccino?",
        answer: "Online ordering is easy: 1) Visit bulaccinocafe.com/menu and browse our menu. 2) Add items to your cart and customize them to your preference. 3) Choose your pickup time. 4) Complete payment securely online. 5) Pick up your fresh order at the cafe! You'll receive SMS and email confirmations with your order details."
      },
      {
        question: "Does Bulaccino Cafe offer delivery?",
        answer: "Currently, Bulaccino Cafe offers pickup orders only. You can order online and pick up at your convenience. We're exploring delivery options for the future - join our loyalty program to be the first to know when delivery becomes available!"
      },
      {
        question: "Can I customize my order at Bulaccino?",
        answer: "Absolutely! Our online ordering system allows full customization. You can choose your coffee size, milk type, add extra shots, select from various add-ons, remove ingredients, and add special instructions. We want your order exactly the way you like it."
      }
    ]
  },
  {
    id: 'loyalty',
    title: 'Loyalty Program',
    icon: Gift,
    faqs: [
      {
        question: "Does Bulaccino Cafe have a loyalty program?",
        answer: "Yes! The Bulaccino Loyalty Program rewards you for every visit. Earn points on every purchase and unlock amazing benefits: Bronze tier (5% discount), Silver tier (10% discount + free birthday treat), Gold tier (15% discount + priority pickup), Platinum tier (20% discount + FREE daily coffee!). Founding members receive exclusive perks and an additional 5% discount."
      },
      {
        question: "How do I join the Bulaccino loyalty program?",
        answer: "Joining is free and easy! You can sign up online at bulaccinocafe.com/loyalty-signup or in-store. Just provide your name, email, and phone number. You'll immediately start earning points and can track your rewards through our website. No app download required!"
      },
      {
        question: "What rewards can I earn at Bulaccino Cafe?",
        answer: "Our loyalty program offers: Discounts from 5% to 20% depending on your tier. Free birthday reward every year. Platinum members get a free coffee every single day. Exclusive member-only offers. Founding members enjoy additional lifetime perks."
      },
      {
        question: "Do loyalty points expire?",
        answer: "Loyalty points remain active as long as you make at least one purchase within 12 months. We want to reward our loyal customers, not penalize occasional visitors. Your tier status is maintained based on your total lifetime spending with us."
      }
    ]
  },
  {
    id: 'location',
    title: 'Location & Hours',
    icon: MapPin,
    faqs: [
      {
        question: "Where is Bulaccino Cafe located?",
        answer: "Bulaccino Cafe is located at Shop 3, Concave Road, Namaka, Nadi, Fiji. We're conveniently located near Nadi International Airport, making us a perfect stop for travelers. We offer a comfortable, air-conditioned space perfect for escaping the tropical heat while enjoying great coffee and food. Visit our Contact page for detailed directions and a map."
      },
      {
        question: "What are Bulaccino Cafe's opening hours?",
        answer: "We're open 7 days a week from 7:00 AM to 6:00 PM. That's Monday through Sunday, 7:00 AM - 6:00 PM. Hours may vary on Fijian public holidays. We recommend checking our website or messaging us on WhatsApp at +679 675 0102 on holidays."
      },
      {
        question: "Is there parking at Bulaccino Cafe?",
        answer: "Yes, convenient parking is available near our cafe. We also welcome walk-ins and are accessible by public transport. Contact us for specific parking directions."
      },
      {
        question: "Is Bulaccino Cafe wheelchair accessible?",
        answer: "Yes, Bulaccino Cafe is wheelchair accessible. We're committed to ensuring all guests can enjoy our space comfortably. Please let us know if you have any specific accessibility needs and we'll be happy to assist."
      }
    ]
  },
  {
    id: 'payment',
    title: 'Payment & Services',
    icon: CreditCard,
    faqs: [
      {
        question: "What payment methods does Bulaccino Cafe accept?",
        answer: "We accept a wide range of payment methods for your convenience: Cash, All major credit and debit cards (Visa, Mastercard), M-PAiSA mobile payments, and other digital payment options. For online orders, secure card payment is available."
      },
      {
        question: "Does Bulaccino Cafe cater for events?",
        answer: "Yes! We offer catering services for corporate events, meetings, weddings, and private functions throughout Fiji. Our catering menu includes coffee service, breakfast platters, lunch boxes, and custom arrangements. Contact us at administrator@bulaccinocafe.com or WhatsApp +679 675 0102 for catering inquiries and quotes."
      },
      {
        question: "Does Bulaccino Cafe have WiFi?",
        answer: "Yes, we offer free high-speed WiFi for all our guests. Whether you're catching up on emails, working remotely, or just browsing, you can stay connected while enjoying your coffee. Ask our staff for the WiFi password."
      },
      {
        question: "Can I book Bulaccino Cafe for a private event?",
        answer: "Yes, we can accommodate private events and functions. Whether it's a birthday celebration, corporate meeting, or special occasion, contact us to discuss your requirements and we'll create a memorable experience for you and your guests."
      }
    ]
  }
];

// Flatten FAQs for schema
const ALL_FAQS = FAQ_CATEGORIES.flatMap(cat => cat.faqs);

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-border last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-5 px-1 text-left hover:text-primary transition-colors"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-foreground pr-4">{faq.question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0"
        >
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        </motion.div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="pb-5 px-1 text-muted-foreground leading-relaxed">
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FAQCategory({ category, openFaqs, setOpenFaqs }) {
  const Icon = category.icon;
  
  return (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <h2 className="font-heading text-2xl text-foreground">{category.title}</h2>
      </div>
      <div className="bg-card rounded-2xl border border-border overflow-hidden">
        {category.faqs.map((faq, idx) => {
          const faqKey = `${category.id}-${idx}`;
          return (
            <FAQItem
              key={faqKey}
              faq={faq}
              isOpen={openFaqs[faqKey]}
              onToggle={() => setOpenFaqs(prev => ({ ...prev, [faqKey]: !prev[faqKey] }))}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function FAQPage() {
  const [openFaqs, setOpenFaqs] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter FAQs based on search
  const filteredCategories = searchQuery
    ? FAQ_CATEGORIES.map(cat => ({
        ...cat,
        faqs: cat.faqs.filter(
          faq =>
            faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.faqs.length > 0)
    : FAQ_CATEGORIES;

  const breadcrumbs = [
    { name: 'Home', url: 'https://bulaccinocafe.com' },
    { name: 'FAQ', url: 'https://bulaccinocafe.com/faq' }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="faq-page">
      <SEO 
        title="FAQ - Frequently Asked Questions | Bulaccino Cafe Fiji"
        description="Find answers to common questions about Bulaccino Cafe, Fiji's premier coffee destination. Learn about our menu, loyalty program, online ordering, location, and more."
        keywords="Bulaccino Cafe FAQ, best cafe Fiji, coffee Fiji, Fiji breakfast, cafe questions, loyalty program Fiji"
        canonical="https://bulaccinocafe.com/faq"
      />
      <FAQSchema customFaqs={ALL_FAQS} />
      <BreadcrumbSchema items={breadcrumbs} />

      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6">
              <HelpCircle className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">How can we help?</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Everything you need to know about Bulaccino Cafe, Fiji's premier coffee destination.
            </p>
            
            {/* Search */}
            <div className="max-w-md mx-auto relative">
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-5 py-4 rounded-2xl bg-card border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground placeholder:text-muted-foreground"
                data-testid="faq-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <span className="sr-only">Clear search</span>
                  &times;
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {filteredCategories.length > 0 ? (
            filteredCategories.map(category => (
              <FAQCategory
                key={category.id}
                category={category}
                openFaqs={openFaqs}
                setOpenFaqs={setOpenFaqs}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No questions found matching "{searchQuery}"</p>
              <Button onClick={() => setSearchQuery('')} variant="outline">
                Clear Search
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-3xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl mb-4">Still have questions?</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Can't find what you're looking for? Our team is here to help!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="rounded-full" data-testid="contact-us-btn">
                  Contact Us
                </Button>
              </Link>
              <Link to="/menu">
                <Button variant="outline" className="rounded-full" data-testid="view-menu-btn">
                  View Our Menu
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links for SEO */}
      <section className="py-8 border-t border-border">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <h3 className="font-heading text-lg mb-4 text-muted-foreground">Popular Topics</h3>
          <div className="flex flex-wrap gap-2">
            {['Best Cafe Fiji', 'Coffee in Fiji', 'Fiji Breakfast', 'Loyalty Program', 'Online Ordering', 'Cafe Hours'].map(topic => (
              <span 
                key={topic}
                className="px-3 py-1.5 bg-muted rounded-full text-sm text-muted-foreground"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
